import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '@byscott-io/corebyscott';
import { useParams, Link } from 'react-router-dom';
import { apps, settings } from '../api/client';

export default function SettingsPage() {
  const { appId } = useParams();
  const [app, setApp] = useState(null);
  const [settingsList, setSettingsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [appRes, settingsRes] = await Promise.all([
        apps.get(appId),
        settings.list(appId),
      ]);
      setApp(appRes.data.app);
      setSettingsList(settingsRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async (setting) => {
    if (!confirm(`Delete setting "${setting.name}"?`)) return;
    try {
      await settings.delete(appId, setting.id);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete setting');
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Loading...</div>;
  if (error) return <div className="p-6"><div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div></div>;

  return (
    <div className="p-6">
      <div className="mb-6 text-sm">
        <Link to="/app/apps" className="text-blue-600 hover:text-blue-800">Apps</Link>
        <span className="mx-2 text-gray-400">/</span>
        <Link to={`/app/apps/${appId}`} className="text-blue-600 hover:text-blue-800">{app?.name}</Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-600">Settings</span>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Settings</h1>
        <button onClick={() => setShowCreateModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
          + New Setting
        </button>
      </div>

      <p className="text-gray-600 mb-6">Settings are JSON configuration values that can be customized per pricing level.</p>

      {settingsList.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <p className="text-gray-500 mb-4">No settings defined yet.</p>
          <button onClick={() => setShowCreateModal(true)} className="text-blue-600 hover:text-blue-800">Create a setting</button>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
          {settingsList.map((setting) => (
            <div key={setting.id} className="p-4 flex justify-between items-center">
              <div>
                <p className="font-medium">{setting.name}</p>
                <p className="text-sm text-gray-500">{setting.key}</p>
                {setting.description && <p className="text-sm text-gray-600 mt-1">{setting.description}</p>}
                {setting.default_value && (
                  <p className="text-sm text-gray-500 mt-1">Default: <code className="bg-gray-100 px-1 rounded">{JSON.stringify(setting.default_value)}</code></p>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditingSetting(setting)} className="text-blue-600 hover:text-blue-800 text-sm">Edit</button>
                <button onClick={() => handleDelete(setting)} className="text-red-600 hover:text-red-800 text-sm">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <SettingModal appId={appId} onClose={() => setShowCreateModal(false)} onSaved={() => { setShowCreateModal(false); loadData(); }} />
      )}

      {editingSetting && (
        <SettingModal appId={appId} setting={editingSetting} onClose={() => setEditingSetting(null)} onSaved={() => { setEditingSetting(null); loadData(); }} />
      )}
    </div>
  );
}

function SettingModal({ appId, setting, onClose, onSaved }) {
  const [key, setKey] = useState(setting?.key || '');
  const [name, setName] = useState(setting?.name || '');
  const [description, setDescription] = useState(setting?.description || '');
  const [defaultValue, setDefaultValue] = useState(setting?.default_value ? JSON.stringify(setting.default_value) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);

      let parsedDefault = null;
      if (defaultValue) {
        try {
          parsedDefault = JSON.parse(defaultValue);
        } catch {
          parsedDefault = defaultValue; // Use as string if not valid JSON
        }
      }

      if (setting) {
        await settings.update(appId, setting.id, { key, name, description, default_value: parsedDefault });
      } else {
        await settings.create(appId, { key, name, description, default_value: parsedDefault });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save setting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={setting ? 'Edit Setting' : 'New Setting'} size="md">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Key</label>
          <input type="text" value={key} onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="e.g., branding_config" required />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="e.g., Branding Configuration" required />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" rows={2} />
        </div>
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Default Value (JSON)</label>
          <textarea value={defaultValue} onChange={(e) => setDefaultValue(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm" rows={3} placeholder='{"key": "value"}' />
        </div>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button type="submit" disabled={saving || !key || !name} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving...' : (setting ? 'Save' : 'Create')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
